import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import {
  ActionButtonOutcomesType,
  BaseWidgetProperties,
  NavigationOptionsType,
  RoleNavigationConfigType,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { catchError, map, of, take } from 'rxjs';
import { UpdateSelectedBeneficiaryFavoriteStatus } from '../../../actions/beneficiary.action';
import { AccountService } from '../../../services/account-service/account-service.service';
import { WidgetsHideConditionsSelector } from '../../../services/hide-conditions-service/hide-conditions-repository';
import { HideConditionsService } from '../../../services/hide-conditions-service/hide-conditions.service';
import { BeneficiaryState } from '../../../state/beneficiary.state';
import { createRedirectionUrlFromNavigationOptions } from '../../utils';

export const markBeneficiaryAsFavoriteOrUnfavorite = (
  widgetProperties: BaseWidgetProperties,
  injector: Injector,
  isFavorite: boolean,
) => {
  const store = injector.get(Store);
  const router = injector.get(Router);
  const accountService = injector.get(AccountService);
  const hideConditionsService = injector.get(HideConditionsService);

  const navigation = widgetProperties?.actionNavigationConfigurations;
  const beneficiaryId = store.selectSnapshot(BeneficiaryState.getBeneficiary)?.selectedBeneficiary as string;

  accountService
    .updateBeneficiaryFavoriteStatus(beneficiaryId, isFavorite)
    .pipe(
      take(1),
      map(() => {
        handleSuccess(
          router,
          store,
          hideConditionsService,
          isFavorite,
          navigation?.[ActionButtonOutcomesType.SUCCESS],
        );
      }),
      catchError((error) => {
        handleFailure(
          router,
          navigation?.[ActionButtonOutcomesType.FAILURE],
          'Favorite/Unfavorite beneficiary action failed',
        );
        return of(error);
      }),
    )
    .subscribe();
};

/**
 * Redirect for success scenario
 */
async function handleSuccess(
  router: Router,
  store: Store,
  hideConditionsService: HideConditionsService,
  isFavorite: boolean,
  redirectConfig?: NavigationOptionsType | RoleNavigationConfigType,
) {
  store.dispatch(new UpdateSelectedBeneficiaryFavoriteStatus(isFavorite));
  hideConditionsService.setHideConditionFromWidget(WidgetsHideConditionsSelector.SELECTED_BENEFICIARY);
  if (redirectConfig) {
    const url = createRedirectionUrlFromNavigationOptions(redirectConfig);
    router.navigateByUrl(url);
  }
}

/**
 * Redirect for failure scenario and logs a Sentry breadcrumb.
 */
function handleFailure(
  router: Router,
  redirectConfig?: NavigationOptionsType | RoleNavigationConfigType,
  logMessage?: string,
) {
  if (redirectConfig) {
    const url = createRedirectionUrlFromNavigationOptions(redirectConfig);
    router.navigateByUrl(url);
  }
  if (logMessage) {
    Sentry.addBreadcrumb({
      category: 'Favorite/Unfavorite beneficiary',
      message: logMessage,
      level: 'error',
    });
  }
}
